import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { saveLastRoute, getLastRoute } from './utils/routePersistence';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import VerifyAccountPage from './components/VerifyAccountPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import TotpSetupPage from './components/TotpSetupPage';
import UnifiedDashboardLayout from './components/UnifiedDashboardLayout';
import DashboardOverview from './components/DashboardOverview';
import VehicleList from './components/VehicleList';
import VehicleDetail from './components/VehicleDetail';
import CustomerList from './components/CustomerList';
import CustomerDetail from './components/CustomerDetail';
import TransactionList from './components/TransactionList';
import TransactionDetail from './components/TransactionDetail';
import PaymentList from './components/PaymentList';
import PaymentDetail from './components/PaymentDetail';
import RTOList from './components/RTOList';
import RTODetail from './components/RTODetail';
import ManagerCalendar from './components/ManagerCalendar';
import AccountSettings from './components/AccountSettings';
import Reports from './components/Reports';
import AssetReport from './components/AssetReport';
import AdminSettings from './components/AdminSettings';
import CustomerDashboard from './components/CustomerDashboard';
import InvestorDashboard from './components/InvestorDashboard';
import { UserList } from './components/UserList';
import { UserCreate } from './components/UserCreate';
import { UserDetail } from './components/UserDetail';
import TaskList from './components/TaskList';
import WorkCalendar from './components/WorkCalendar';
import SettingsLayout from './components/SettingsLayout';
import { AuditExport } from './components/AuditExport';
import ReportsLayout from './components/ReportsLayout';
import LocationRevenueChart from './components/LocationRevenueChart';

/**
 * Main app component with authentication and role-based routing
 * Routes to appropriate dashboard based on user role
 */
function AppWithAuth() {
  const auth = useAuth();
  const location = useLocation();

  // Enable automatic token refresh when authenticated
  useTokenRefresh();

  // Save route on navigation
  useEffect(() => {
    if (auth.isAuthenticated && !auth.loading && location.pathname !== '/') {
      saveLastRoute(location.pathname);
    }
  }, [location.pathname, auth.isAuthenticated, auth.loading]);

  // Loading state
  if (auth.loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading application...
      </div>
    );
  }

  // Not authenticated - public auth routes
  if (!auth.isAuthenticated) {
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify" element={<VerifyAccountPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // Authenticated - route based on role
  if (auth.user?.role === 'admin' || auth.user?.role === 'manager' || auth.user?.role === 'auditor') {
    return (
      <Routes>
        <Route path="/mfa-setup" element={<TotpSetupPage />} />
        <Route path="dashboard" element={<UnifiedDashboardLayout />}>
          <Route index element={<DashboardOverview />} />
          <Route path="vehicles" element={<VehicleList />} />
          <Route path="vehicles/:id" element={<VehicleDetail />} />
          <Route path="customers" element={<CustomerList />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="transactions" element={<TransactionList />} />
          <Route path="transactions/:id" element={<TransactionDetail />} />
          <Route path="payments" element={<PaymentList />} />
          <Route path="payments/:id" element={<PaymentDetail />} />
          <Route path="rto" element={<RTOList />} />
          <Route path="rto/:id" element={<RTODetail />} />
          <Route path="calendar" element={<ManagerCalendar />} />
          <Route path="tasks" element={<TaskList />} />
          <Route path="work-calendar" element={<WorkCalendar />} />
          <Route path="reports" element={<ReportsLayout />}>
            <Route index element={<Navigate to="monthly-analysis" replace />} />
            <Route path="monthly-analysis" element={<Reports />} />
            <Route path="asset" element={<AssetReport />} />
            <Route path="revenue" element={<LocationRevenueChart />} />
          </Route>
          <Route path="asset-report" element={<Navigate to="/dashboard/reports/asset" replace />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="account" replace />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="export" element={<AuditExport />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/create" element={<UserCreate />} />
            <Route path="users/:id" element={<UserDetail />} />
            <Route path="general" element={<AdminSettings />} />
          </Route>
        </Route>
        <Route path="/" element={
          <Navigate to={
            // If on app subdomain, always go to dashboard
            (window.location.hostname === 'app.tegratauto.com' ||
             window.location.hostname === 'app-dev.tegratauto.com')
              ? 'dashboard'
              : (getLastRoute() || "dashboard")
          } replace />
        } />
      </Routes>
    );
  }

  if (auth.user?.role === 'investor') {
    return (
      <Routes>
        <Route path="/mfa-setup" element={<TotpSetupPage />} />
        <Route path="*" element={<InvestorDashboard />} />
      </Routes>
    );
  }

  if (auth.user?.role === 'customer') {
    return (
      <Routes>
        <Route path="/mfa-setup" element={<TotpSetupPage />} />
        <Route path="*" element={<CustomerDashboard />} />
      </Routes>
    );
  }

  // Unknown role
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f5f7fa'
    }}>
      <div style={{ textAlign: 'center' }}>
        <h1>Unknown Role</h1>
        <p>Your account role could not be determined.</p>
        <button 
          onClick={() => auth.logout()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Logout and Try Again
        </button>
      </div>
    </div>
  );
}

export default AppWithAuth;
